/**
 * Pet growth helpers. Pure functions over a `petState` shaped:
 *
 *   { growth: number, stage: number }
 *
 * `growth` is the running total of growth points the child has accumulated
 * since planting the seed; `stage` is the highest stage reached so far
 * (never decreases — once mastered, a stage is yours).
 */

import { PET_STAGES } from '../data/petStages.js';

export function initialPetState() {
  return { growth: 0, stage: 0 };
}

/**
 * Find the highest stage whose threshold is ≤ growth.
 */
export function stageForGrowth(growth) {
  let stage = PET_STAGES[0];
  for (const s of PET_STAGES) {
    if (growth >= s.threshold) stage = s;
    else break;
  }
  return stage;
}

/**
 * Apply a positive growth amount. Returns the next pet state PLUS the
 * stage id that was just reached (or null when no stage change happened).
 */
export function addGrowth(state, points) {
  const safe = state ?? initialPetState();
  const inc = Math.max(0, Math.floor(points ?? 0));
  const nextGrowth = safe.growth + inc;
  const nextStage = stageForGrowth(nextGrowth).id;
  const stageChanged = nextStage > (safe.stage ?? 0);
  return {
    state: {
      growth: nextGrowth,
      stage: Math.max(safe.stage ?? 0, nextStage),
    },
    stageChanged,
    newStageId: stageChanged ? nextStage : null,
  };
}

/**
 * UI helper — returns the current stage descriptor + progress (0..1) toward
 * the next stage. When already at the final stage, `progressToNext` is 1.
 */
export function describePet(state) {
  const safe = state ?? initialPetState();
  const current = stageForGrowth(safe.growth);
  const currentIdx = PET_STAGES.findIndex((s) => s.id === current.id);
  const next = PET_STAGES[currentIdx + 1] ?? null;
  if (!next) {
    return {
      current,
      next: null,
      progressToNext: 1,
      growthToNext: 0,
    };
  }
  const span = next.threshold - current.threshold;
  const progressed = safe.growth - current.threshold;
  return {
    current,
    next,
    progressToNext: Math.min(1, Math.max(0, progressed / span)),
    growthToNext: Math.max(0, next.threshold - safe.growth),
  };
}
